import React, { useState, useRef } from 'react';
import clsx from 'clsx';
import PhotoSwipe from 'photoswipe';
import 'photoswipe/dist/photoswipe.css';

// Comps
import { Image } from './GalleryImage';
import Video from '@project-stories/elements/Video/Video';
import SwiperSlider from '@project-stories/elements/SwiperSlider/SwiperSlider';

// Libs
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { Thumbs } from 'swiper/modules';

// Hooks
import useVideoSource from '@arctheme-hooks/useVideoSourceQuality';
import { useSwiperModulesDisabledClassToggle } from '@src/hooks/useSwiperModulesDisabledClassToggle';

// Types
import { ILiquidMedia } from '@src/types/product';
import { PhotoswipeLeftIcon, PhotoswipeRightIcon, PhotoswipeCloseIcon } from '@project-stories/elements/Icons';

type Props = {
  media: ILiquidMedia[];
  settings: any;
  responsive_images: any;
  isMobile: boolean;
};

const ProductGalleryCarouselWithThumbnail = ({
  media,
  settings,
  isMobile
}: Props) => {
  const {
    show_navigation_arrows,
    show_navigation_arrows_mobile,
    show_pagination,
    show_pagination_mobile,
    autoplay_video,
    border_radius,
    border_radius_mobile,
    border_radius_thumbnail,
    initial_image,
    thumbnail_gallery_gap,
    thumbnail_gallery_gap_mobile
  } = settings.section;
  
  const { responsive_images } = settings;

  // States
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperRef | null>(null);

  // Refs
  const swiperRef = useRef(null);
  const videoRefs = useRef([]);
  const pswpRef = useRef<PhotoSwipe | null>(null);
  console.log(`${PhotoswipeLeftIcon()}`, 'left icon');

  const openPhotoSwipe = (index: number) => {
    const items = media.map(item => ({
      src: item.media_type === 'image' ? item.src : item.preview_image.src,
      w: item.media_type === 'image' ? item.width : item.preview_image.width,
      h: item.media_type === 'image' ? item.height : item.preview_image.height,
      msrc: item.media_type === 'image' ? item.src : item.preview_image.src,
    }));

    const options = {
      index,
      closeOnScroll: true,
      history: false,
      showAnimationDuration: 333,
      hideAnimationDuration: 333,
      zoom: false,
      close: true,
      counter: false,
      arrowPrev: true,
      arrowNext: true,
      arrowPrevSVG: PhotoswipeLeftIcon(),
      arrowNextSVG: PhotoswipeRightIcon(),
      closeSVG: PhotoswipeCloseIcon(),
    };

    pswpRef.current = new PhotoSwipe({
      dataSource: items,
      ...options,
      pswpModule: PhotoSwipe
    });

    pswpRef.current.init();
  };

  // Hooks
  const { swiperDisabledClass } = useSwiperModulesDisabledClassToggle({
    navigation: {
      mobile: show_navigation_arrows_mobile,
      desktop: show_navigation_arrows
    },
    pagination: {
      mobile: show_pagination_mobile,
      desktop: show_pagination
    }
  });

  const videoSources = media.map((obj) => {
    if (obj.media_type === 'video') {
      return useVideoSource(
        obj.sources,
        obj.preview_image,
        isMobile,
        settings.video_quality
      );
    }
    return null;
  });

  return (
    <>
      <div className='product-gallery relative'>
        <div className='flex max-lg:flex-col lg:items-center' style={{ gap: isMobile ? thumbnail_gallery_gap_mobile : thumbnail_gallery_gap }}>
        {/* Thumbnails */}
        <div
          className={`lg:h-[24.72vw] lg:max-h-[356px] lg:w-[4.2vw] lg:max-w-[60px] shrink-0 lg:mr-4 max-lg:order-1`}
        >

          <Swiper
            className='h-full swiper--thumbnails overflow-hidden'
            modules={[Thumbs]}
            watchSlidesProgress
            onSwiper={setThumbsSwiper}
            slidesPerView={3.78}
            spaceBetween={12}
            initialSlide={initial_image - 1}
    
            loop={media.length > 3}
            draggable={true}
            breakpoints={{
              768: {
                slidesPerView: 4.78,
                loop: media.length > 4
              },
              1024: {
                direction: 'vertical',
                spaceBetween: 14,
                slidesPerView: 5,
                loop: media.length > 5
              }
            }}
          >
            {media.map((obj) => (
              <SwiperSlide
                key={`${obj.id}--thumbnail`}
                className={`rounded-[${border_radius_thumbnail}px] overflow-hidden`}
                     
              >
                <Image
                  imageUrl={obj.preview_image.src}
                  alt={obj.alt}
                  classes='w-full object-cover aspect-square'
                  isLazy={false}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Main */}
        <SwiperSlider
          containerClass={`${swiperDisabledClass} w-full h-full`}
          className={`swiper--main rounded-[${border_radius_mobile}px] lg:rounded-[${border_radius}px]`}
          ref={swiperRef}
          navigation={{
            enabled: show_navigation_arrows_mobile
          }}
          pagination={{
            enabled: show_pagination_mobile
          }}
          thumbs={{
            swiper:
              thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null
      
          }}         
           onSlideChange={(swiper) => {
            const currIndex = swiper.realIndex; 
              if(thumbsSwiper){ 
                thumbsSwiper.slideToLoop(currIndex,  300)
              } 

            if (!autoplay_video) return;

            const activeVideo = videoRefs.current[swiper.realIndex];
            const previousVideo = videoRefs.current[swiper.previousRealIndex];

            // Play if active
            if (activeVideo) activeVideo.play();

            // Pause if inactive
            if (previousVideo) previousVideo.pause();
            }}
          
          initialSlide={initial_image - 1}
          slidesPerView={1}
          spaceBetween={0}
          loop={true}
          
          breakpoints={{
            1024: {
              navigation: {
                enabled: show_navigation_arrows
              },
              pagination: {
                enabled: show_pagination,
                clickable: true
              }
            }
          }}
 
        >
          {media.map((obj, index) => {
            const videoSrc = videoSources[index]?.videoUrl || '';
            const imageToRenderSet = responsive_images[index]?.srcset
            return (
              <div
                key={obj.id}
                className='w-full'
              >
                {obj.media_type === 'image' ? (
                  <div 
                    onClick={() => isMobile ? openPhotoSwipe(index) : undefined}
                    className={clsx("w-full", { "cursor-zoom-in": isMobile })}
                  >
                    <Image
                      imageUrl={obj.src}
                      alt={obj.alt}
                      imageUrlSet={imageToRenderSet}
                      classes='w-full object-cover aspect-square'
                      isLazy={index !== 0}
                    />
                  </div>
                ) : (
                  <Video
                    key={obj.id}
                    ref={(ref) => (videoRefs.current[index] = ref)}
                    src={videoSrc}
                    isAutoplay={false}
                    classes='pointer-events-none'
                    isLazyLoad={index === 0 ? false : true}
                    poster={obj.preview_image.src}
                    width={obj.preview_image.width}
                    height={obj.preview_image.height}
                    showControls={true}
                  />
                )}
              </div>
            );
          })}
        </SwiperSlider>
      </div>
      
      {/* PhotoSwipe root element */}
      <div className="pswp" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="pswp__bg"></div>
        <div className="pswp__scroll-wrap">
          <div className="pswp__container">
            <div className="pswp__item"></div>
            <div className="pswp__item"></div>
            <div className="pswp__item"></div>
          </div>
          <div className="pswp__ui pswp__ui--hidden">
            <div className="pswp__top-bar">
              <div className="nav-buttons">
                <button className="pswp__button pswp__button--arrow--left nav-button prev" title="Previous"></button>
                <button className="pswp__button pswp__button--close" title="Close"></button>
                <button className="pswp__button pswp__button--arrow--right nav-button next" title="Next"></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </>
  );
};

export default ProductGalleryCarouselWithThumbnail;
